<template>
  <b-modal
    :id="id"
    size="xl"
    scrollable
    modal-class="db-modal"
    body-class="si-body"
    @show="showModal"
    @hidden="hiddenModal"
  >
    <template #modal-header>
      <div class="db-modal-head">
        <h2 class="db-modal-title">{{ idGallery ? "Fotos de la galería" : multiSelect ? "Seleccionar imágenes" : "Seleccionar imagen" }}</h2>
        <p class="db-modal-subtitle">
          {{ multiSelect ? "Pulsa sobre las fotos para marcarlas o desmarcarlas." : "Pulsa sobre la foto que quieras usar." }}
        </p>
      </div>
      <button type="button" class="db-btn db-btn--ghost db-btn--sm" :disabled="subiendo > 0" @click="$refs.fileInput.click()">
        <span class="material-symbols-outlined">upload</span> Subir fotos
      </button>
      <button type="button" class="db-icon-btn" aria-label="Cerrar" @click="closeModal">
        <span class="material-symbols-outlined">close</span>
      </button>
    </template>

    <div
      class="si-drop"
      :class="{ 'is-dragging': arrastrando }"
      @dragenter.prevent="arrastrando = true"
      @dragover.prevent="arrastrando = true"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <div v-if="subiendo > 0" class="si-uploading" role="status">
        <span class="db-spinner"></span>
        Subiendo {{ subiendo }} {{ subiendo === 1 ? "imagen" : "imágenes" }}…
      </div>

      <div v-if="cargando" class="si-grid">
        <div v-for="n in 12" :key="n" class="db-skeleton si-skeleton"></div>
      </div>

      <div v-else-if="!listaGallery.length" class="db-empty">
        <span class="material-symbols-outlined">add_photo_alternate</span>
        <p>No hay imágenes todavía. Sube la primera o arrástrala aquí.</p>
      </div>

      <div v-else class="si-grid">
        <cardViewSelector
          v-for="item in listaGallery"
          :key="item.id"
          :imageSrc="item.imagen"
          :imageId="item.id"
          :orden="ordenSeleccion(item)"
          :multiSelect="multiSelect"
          :puedeEliminar="puedeEliminar"
          @selectedImage="toggle(item)"
          @eliminarImagen="eliminarImagen"
        />
      </div>

      <div v-if="arrastrando" class="si-drop-hint">
        <span class="material-symbols-outlined">cloud_upload</span>
        Suelta las imágenes para subirlas
      </div>
    </div>

    <input
      ref="fileInput"
      accept=".jpeg, .jpg, .png, .webp"
      type="file"
      class="si-file"
      multiple
      @change="newImage"
    />

    <template #modal-footer>
      <span class="db-count">
        <template v-if="selectedImages.length">
          {{ selectedImages.length }} seleccionada{{ selectedImages.length === 1 ? "" : "s" }}
        </template>
        <template v-else>{{ listaGallery.length }} imágenes</template>
      </span>
      <button v-if="selectedImages.length && multiSelect" type="button" class="db-btn db-btn--ghost db-btn--sm" @click="selectedImages = []">
        Quitar selección
      </button>
      <span class="db-toolbar-spacer"></span>
      <button type="button" class="db-btn db-btn--ghost" @click="closeModal">Cancelar</button>
      <button type="button" class="db-btn" :disabled="guardando || (!multiSelect && !selectedImages.length)" @click="updateGallery">
        <span v-if="guardando" class="db-spinner"></span>
        {{ idGallery ? "Guardar galería" : multiSelect ? "Usar selección" : "Usar imagen" }}
      </button>
    </template>
  </b-modal>
</template>

<script>
import axios from "@/directives/axios.js";
import cardViewSelector from "@/components/dashboard/CardViewSelector.vue";
import imageCompression from "browser-image-compression";
import { confirmar, avisar, PERMISOS, avisarError } from "@/directives/dbDialogs.js";

// Mismos tamaños que usa el backend (App\Support\Miniaturas) al regenerar miniaturas
const COMPRESION = {
  miniatura: { maxWidthOrHeight: 640, maxSizeMB: 0.08, initialQuality: 0.72 },
  grande: { maxWidthOrHeight: 1920, maxSizeMB: 0.5, initialQuality: 0.8 },
};

// ¿Sabe este navegador generar WebP? (Safari < 16 no: devolvería PNG)
let webpSoportado = null;
function navegadorGeneraWebp() {
  if (webpSoportado === null) {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = canvas.height = 1;
      webpSoportado = canvas.toDataURL("image/webp").startsWith("data:image/webp");
    } catch (e) {
      webpSoportado = false;
    }
  }
  return webpSoportado;
}

// Formato de subida: WebP (~30 % menos que JPEG y conserva transparencias).
// Si el navegador no lo genera: JPEG para fotos y PNG si el original es PNG.
function formatoSubida(file) {
  if (navegadorGeneraWebp()) return "image/webp";
  return file.type === "image/png" ? "image/png" : "image/jpeg";
}

export default {
  name: "selectorImagenes",
  props: {
    dataGallery: Array,
    idGallery: [String, Number],
    multiSelect: Boolean,
    id: String,
  },

  components: {
    cardViewSelector,
  },

  data() {
    return {
      subiendo: 0,
      subidasOk: 0,
      cargando: false,
      guardando: false,
      arrastrando: false,
      listaGallery: [],
      selectedImages: [],
    };
  },
  computed: {
    // Borrar imágenes del servidor exige el permiso de galerías (subirlas no).
    puedeEliminar() {
      return this.$store.getters.puede(PERMISOS.GALERIAS);
    },
  },
  methods: {
    ordenSeleccion(item) {
      const i = this.selectedImages.findIndex((s) => s && s.imagen === item.imagen);
      return i === -1 ? 0 : i + 1;
    },
    toggle(item) {
      if (this.ordenSeleccion(item)) {
        this.selectedImages = this.selectedImages.filter((s) => s.imagen !== item.imagen);
      } else if (this.multiSelect) {
        this.selectedImages.push(item);
      } else {
        this.selectedImages = [item];
      }
    },
    // Recupera la selección previa (nombres de imagen) sobre la lista actual
    aplicarSeleccionPrevia() {
      if (Array.isArray(this.dataGallery)) {
        this.selectedImages = this.listaGallery.filter((img) => this.dataGallery.includes(img.imagen));
      }
    },
    async eliminarImagen(element) {
      const ok = await confirmar(this, "La imagen se borrará definitivamente del servidor. Si está en una galería, en una noticia o es una portada, no se podrá borrar.", {
        titulo: "Eliminar imagen",
        ok: "Eliminar",
        peligro: true,
      });
      if (!ok) return;
      try {
        const result = await axios.delete("/imagenes/eliminarImagen", {
          data: { img: element.src, id: element.index },
        });
        this.listaGallery = result.data.imagenes || [];
        this.selectedImages = this.selectedImages.filter((s) => s.imagen !== element.src);
        avisar(this, "La imagen se ha borrado del servidor.", "success", "Imagen eliminada");
        this.$store.dispatch("changeAllImages").catch(() => {});
      } catch (error) {
        avisarError(this, error, "No se pudo eliminar la imagen.");
      }
    },
    hiddenModal() {
      this.listaGallery = [];
      this.arrastrando = false;
    },
    showModal() {
      this.selectedImages = [];
      this.changeImages(this.idGallery);
    },
    async updateGallery() {
      if (!this.idGallery) {
        this.$emit("imagen", this.selectedImages);
        this.closeModal();
        return;
      }
      this.guardando = true;
      try {
        await axios.post("/imagenes/actualizaImagenesGaleria", {
          id: this.idGallery,
          listImages: this.selectedImages.map((element) => element.id),
        });
        avisar(this, `La galería tiene ahora ${this.selectedImages.length} ${this.selectedImages.length === 1 ? "foto" : "fotos"}.`, "success", "Galería actualizada");
        this.$emit("guardado");
        this.closeModal();
      } catch (error) {
        avisarError(this, error, "No se pudo guardar la galería.");
      } finally {
        this.guardando = false;
      }
    },
    async changeImages(gallery) {
      this.cargando = true;
      try {
        const { data } = await axios.get("/imagenes");
        this.listaGallery = data.imagenes || [];
        if (gallery) {
          const galeria = await axios.get(`/imagenes/galeriaImagenes?id=${gallery}`);
          const enGaleria = ((galeria.data.galerias && galeria.data.galerias.imagenes) || []).map((i) => i.imagen);
          this.selectedImages = this.listaGallery.filter((img) => enGaleria.includes(img.imagen));
        } else {
          this.aplicarSeleccionPrevia();
        }
      } catch (error) {
        avisarError(this, error, "No se pudieron cargar las imágenes.");
      } finally {
        this.cargando = false;
      }
    },
    closeModal() {
      this.$bvModal.hide(this.id);
    },
    onDragLeave(e) {
      if (!e.currentTarget.contains(e.relatedTarget)) this.arrastrando = false;
    },
    onDrop(e) {
      this.arrastrando = false;
      const files = Array.from(e.dataTransfer.files || []).filter((f) => /image\/(jpe?g|png|webp)/.test(f.type));
      if (!files.length) {
        avisar(this, "Solo se pueden subir imágenes JPG, PNG o WebP.", "warning");
        return;
      }
      this.subirArchivos(files);
    },
    newImage(event) {
      this.subirArchivos(Array.from(event.target.files || []));
      event.target.value = "";
    },
    subirArchivos(files) {
      this.subiendo += files.length;
      files.forEach((file) => this.uploadImage(file));
    },
    convertToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    },
    async uploadImage(file) {
      const quitarCabecera = (b64) => b64.replace(/^data:image\/(jpg|jpeg|png|webp);base64,/, "");
      let imageBig;
      let imageLitel;
      let name;

      try {
        // Las dos versiones van en el mismo formato (lo exige la API): WebP siempre
        // que el navegador sepa generarlo.
        const formato = formatoSubida(file);
        const comunes = { useWebWorker: true, fileType: formato, preserveExif: false };

        // Miniatura: rejillas y tarjetas (≤ 640 px, ~35-45 KB)
        const pequena = await imageCompression(file, {
          ...comunes,
          ...COMPRESION.miniatura,
        });
        // Grande: pantalla completa, con buena calidad pero ligera (≤ ~500 KB)
        const grande = await imageCompression(file, {
          ...comunes,
          ...COMPRESION.grande,
        });
        imageLitel = quitarCabecera(await this.convertToBase64(pequena));
        imageBig = quitarCabecera(await this.convertToBase64(grande));
        name = ("sd" + Math.trunc(Math.random() * 900 + 100) + file.name + ".jpg").replace(/ /g, "_");
      } catch (error) {
        avisar(this, `No se pudo comprimir «${file.name}». Puede que el archivo esté dañado o no sea una imagen.`, "danger");
        this.finSubida(false);
        return;
      }

      try {
        const result = await axios.post("/imagenes/nuevaImagen", {
          imageLitel,
          imageBig,
          nameImage: name,
        });
        const seleccion = this.selectedImages.map((s) => s.imagen);
        const idsPrevios = new Set(this.listaGallery.map((img) => img.id));
        this.listaGallery = result.data.imagenes || this.listaGallery;
        // Mantiene la selección actual y marca la nueva imagen. El servidor renombra
        // el fichero, así que la nueva se reconoce por ser el id que no estaba antes.
        this.selectedImages = this.listaGallery.filter((img) => seleccion.includes(img.imagen));
        const nueva =
          this.listaGallery.find((img) => !idsPrevios.has(img.id) && !seleccion.includes(img.imagen)) ||
          this.listaGallery.find((img) => img.imagen === name);
        if (nueva && (this.multiSelect || !this.selectedImages.length)) this.toggle(nueva);
        this.finSubida(true);
      } catch (error) {
        avisarError(this, error, `No se pudo subir «${file.name}».`);
        this.finSubida(false);
      }
    },
    // Cuando termina la última imagen de la tanda se muestra un único aviso
    finSubida(ok) {
      if (ok) this.subidasOk += 1;
      this.subiendo -= 1;
      if (this.subiendo > 0) return;

      if (this.subidasOk > 0) {
        const n = this.subidasOk;
        avisar(
          this,
          n === 1 ? "La imagen se ha subido y ya está seleccionada." : `Se han subido ${n} imágenes y ya están seleccionadas.`,
          "success",
          n === 1 ? "Imagen subida" : "Imágenes subidas"
        );
        this.$store.dispatch("changeAllImages").catch(() => {});
      }
      this.subidasOk = 0;
    },
  },
};
</script>

<style scoped>
.si-drop {
  position: relative;
  min-height: 300px;
}

.si-drop.is-dragging {
  outline: 2px dashed var(--sd-primary);
  outline-offset: 6px;
  border-radius: 12px;
}

.si-drop-hint {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--sd-primary-soft) 92%, transparent);
  font-weight: 600;
  color: var(--sd-primary);
  pointer-events: none;
}

.si-drop-hint .material-symbols-outlined {
  font-size: 44px;
}

.si-uploading {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding: 10px 14px;
  border-radius: 10px;
  background: var(--sd-primary-soft);
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--sd-primary);
}

.si-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
}

.si-skeleton {
  aspect-ratio: 4 / 3;
}

.si-file {
  display: none;
}
</style>
